/**
 * Offline Storage Service - IndexedDB ile sipariş cache'leme
 * Siparişleri offline durumda görüntülemek için kullanılır
 */

const DB_NAME = 'MenomBusinessPanel';
const DB_VERSION = 1;
const STORE_ORDERS = 'orders';
const STORE_ACTIVE_ORDERS = 'activeOrders';
const STORE_COMPLETED_ORDERS = 'completedOrders';

class OfflineStorage {
  constructor() {
    this.db = null;
  }

  // IndexedDB bağlantısını aç
  async openDB() {
    if (this.db) {
      return this.db;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB açılamadı:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Orders store (tüm siparişler)
        if (!db.objectStoreNames.contains(STORE_ORDERS)) {
          const ordersStore = db.createObjectStore(STORE_ORDERS, { keyPath: '_id' });
          ordersStore.createIndex('status', 'status', { unique: false });
          ordersStore.createIndex('createdAt', 'createdAt', { unique: false });
          ordersStore.createIndex('restaurantId', 'restaurantId', { unique: false });
        }

        // Active orders store
        if (!db.objectStoreNames.contains(STORE_ACTIVE_ORDERS)) {
          db.createObjectStore(STORE_ACTIVE_ORDERS, { keyPath: '_id' });
        }

        // Completed orders store
        if (!db.objectStoreNames.contains(STORE_COMPLETED_ORDERS)) {
          db.createObjectStore(STORE_COMPLETED_ORDERS, { keyPath: '_id' });
        }
      };
    });
  }

  // Aktif siparişleri kaydet
  async saveActiveOrders(orders) {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([STORE_ACTIVE_ORDERS], 'readwrite');
      const store = transaction.objectStore(STORE_ACTIVE_ORDERS);

      // Önce mevcut verileri temizle (Promise'e çevir)
      await new Promise((resolve, reject) => {
        const clearRequest = store.clear();
        clearRequest.onsuccess = () => resolve();
        clearRequest.onerror = () => reject(clearRequest.error);
      });

      // Yeni siparişleri ekle
      for (const order of orders) {
        // _id veya id alanını normalize et
        const normalizedOrder = {
          ...order,
          _id: order._id || order.id || `temp_${Date.now()}_${Math.random()}`
        };
        
        await new Promise((resolve, reject) => {
          const putRequest = store.put(normalizedOrder);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        });
      }

      // Transaction tamamlanmasını bekle
      await new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });

      // Tüm siparişleri de ana store'a kaydet (normalize edilmiş)
      const normalizedOrders = orders.map(order => ({
        ...order,
        _id: order._id || order.id || `temp_${Date.now()}_${Math.random()}`
      }));
      await this.saveOrders(normalizedOrders);

      return true;
    } catch (error) {
      console.error('❌ Aktif siparişler kaydedilemedi:', error);
      return false;
    }
  }

  // Tamamlanmış siparişleri kaydet
  async saveCompletedOrders(orders) {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([STORE_COMPLETED_ORDERS], 'readwrite');
      const store = transaction.objectStore(STORE_COMPLETED_ORDERS);

      // Önce mevcut verileri temizle (Promise'e çevir)
      await new Promise((resolve, reject) => {
        const clearRequest = store.clear();
        clearRequest.onsuccess = () => resolve();
        clearRequest.onerror = () => reject(clearRequest.error);
      });

      // Yeni siparişleri ekle
      for (const order of orders) {
        // _id veya id alanını normalize et
        const normalizedOrder = {
          ...order,
          _id: order._id || order.id || `temp_${Date.now()}_${Math.random()}`
        };
        
        await new Promise((resolve, reject) => {
          const putRequest = store.put(normalizedOrder);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        });
      }

      // Transaction tamamlanmasını bekle
      await new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });

      // Tüm siparişleri de ana store'a kaydet (normalize edilmiş)
      const normalizedOrders = orders.map(order => ({
        ...order,
        _id: order._id || order.id || `temp_${Date.now()}_${Math.random()}`
      }));
      await this.saveOrders(normalizedOrders);

      return true;
    } catch (error) {
      console.error('❌ Tamamlanmış siparişler kaydedilemedi:', error);
      return false;
    }
  }

  // Tüm siparişleri kaydet
  async saveOrders(orders) {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([STORE_ORDERS], 'readwrite');
      const store = transaction.objectStore(STORE_ORDERS);

      for (const order of orders) {
        await new Promise((resolve, reject) => {
          const putRequest = store.put(order);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        });
      }

      // Transaction tamamlanmasını bekle
      await new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });

      return true;
    } catch (error) {
      console.error('❌ Siparişler kaydedilemedi:', error);
      return false;
    }
  }

  // Aktif siparişleri getir
  async getActiveOrders() {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([STORE_ACTIVE_ORDERS], 'readonly');
      const store = transaction.objectStore(STORE_ACTIVE_ORDERS);
      
      return new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => {
          resolve(request.result || []);
        };
        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Aktif siparişler getirilemedi:', error);
      return [];
    }
  }

  // Tamamlanmış siparişleri getir
  async getCompletedOrders() {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([STORE_COMPLETED_ORDERS], 'readonly');
      const store = transaction.objectStore(STORE_COMPLETED_ORDERS);
      
      return new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => {
          resolve(request.result || []);
        };
        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Tamamlanmış siparişler getirilemedi:', error);
      return [];
    }
  }

  // Tek bir sipariş ekle/güncelle
  async saveOrder(order) {
    try {
      // _id veya id alanını normalize et
      const normalizedOrder = {
        ...order,
        _id: order._id || order.id || `temp_${Date.now()}_${Math.random()}`
      };
      
      const db = await this.openDB();
      const transaction = db.transaction([STORE_ORDERS, STORE_ACTIVE_ORDERS, STORE_COMPLETED_ORDERS], 'readwrite');
      
      const ordersStore = transaction.objectStore(STORE_ORDERS);
      await new Promise((resolve, reject) => {
        const putRequest = ordersStore.put(normalizedOrder);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      });

      // Duruma göre ilgili store'a da ekle
      if (['pending', 'preparing', 'ready'].includes(normalizedOrder.status)) {
        const activeStore = transaction.objectStore(STORE_ACTIVE_ORDERS);
        await new Promise((resolve, reject) => {
          const putRequest = activeStore.put(normalizedOrder);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        });
        
        // Tamamlanmış store'dan sil
        const completedStore = transaction.objectStore(STORE_COMPLETED_ORDERS);
        await new Promise((resolve, reject) => {
          const deleteRequest = completedStore.delete(normalizedOrder._id);
          deleteRequest.onsuccess = () => resolve();
          deleteRequest.onerror = () => reject(deleteRequest.error);
        });
      } else if (['delivered', 'cancelled'].includes(normalizedOrder.status)) {
        const completedStore = transaction.objectStore(STORE_COMPLETED_ORDERS);
        await new Promise((resolve, reject) => {
          const putRequest = completedStore.put(normalizedOrder);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        });
        
        // Aktif store'dan sil
        const activeStore = transaction.objectStore(STORE_ACTIVE_ORDERS);
        await new Promise((resolve, reject) => {
          const deleteRequest = activeStore.delete(normalizedOrder._id);
          deleteRequest.onsuccess = () => resolve();
          deleteRequest.onerror = () => reject(deleteRequest.error);
        });
      }

      // Transaction tamamlanmasını bekle
      await new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });

      return true;
    } catch (error) {
      console.error('❌ Sipariş kaydedilemedi:', error);
      return false;
    }
  }

  // Cache'i temizle
  async clearCache() {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([STORE_ORDERS, STORE_ACTIVE_ORDERS, STORE_COMPLETED_ORDERS], 'readwrite');
      
      await transaction.objectStore(STORE_ORDERS).clear();
      await transaction.objectStore(STORE_ACTIVE_ORDERS).clear();
      await transaction.objectStore(STORE_COMPLETED_ORDERS).clear();

      return true;
    } catch (error) {
      console.error('Cache temizlenemedi:', error);
      return false;
    }
  }

  // Cache son güncelleme zamanını kaydet
  async setLastUpdateTime() {
    try {
      localStorage.setItem('orders_last_update', new Date().toISOString());
      return true;
    } catch (error) {
      console.error('Son güncelleme zamanı kaydedilemedi:', error);
      return false;
    }
  }

  // Cache son güncelleme zamanını getir
  getLastUpdateTime() {
    try {
      return localStorage.getItem('orders_last_update');
    } catch (error) {
      return null;
    }
  }
}

// Singleton instance
const offlineStorage = new OfflineStorage();

export default offlineStorage;

